/**
 * The single table of what may be attached to a chat message.
 *
 * Every layer that touches an upload needs the same verdict: the composer
 * filters the file picker and rejects a drop before it costs a round trip, the
 * websocket route re-decides it on bytes it does not trust, the attachment
 * store derives the on-disk extension from it, and the HTTP route decides from
 * it whether the file may be served inline. Four copies of that table would
 * drift into one layer accepting what another rejects — and the layer that
 * loses is the route serving unauthenticated bytes on the app's own origin.
 *
 * Two rules are load-bearing rather than cosmetic:
 *
 * 1. **`DENIED_ATTACHMENT_EXTENSIONS` wins over everything.** `/attachments/<id>`
 *    is reachable by id with no session, on the same origin the app's cookie
 *    lives on. An `.html` or `.svg` served from there is stored XSS, whatever
 *    content-type it carries, because a browser navigated straight to it runs
 *    it. They are refused at upload rather than patched at serve time.
 * 2. **The mime type must agree with the file name.** A `.zip` announced as
 *    `application/pdf` is either a confused client or a deliberate attempt to
 *    have the extension and the content-type disagree, and the store names the
 *    file from one while the route serves it from the other.
 *
 * Nothing here inspects bytes: the browser's `File.type` is a hint, not proof.
 * Where the content itself must be proven — the agent's screenshot tool, which
 * names a path in a worktree it also writes — the server sniffs the leading
 * bytes instead (`detectImageMimeFromBytes`). This table governs what a person
 * is allowed to hand us; that function governs what we are willing to believe.
 */

import {
  PROVIDER_SEND_TURN_MAX_FILE_BYTES,
  PROVIDER_SEND_TURN_MAX_IMAGE_BYTES,
} from "@taskin-code/contracts";

/** What kind of thing an attachment is, which is what its limits follow from. */
export type AttachmentKind = "image" | "document" | "text" | "archive";

/**
 * The ceilings live in `contracts` because the schemas enforce them on the
 * wire; re-exported here so a caller reasoning about kinds never has to import
 * both modules to learn what a kind is allowed to weigh.
 */
export const ATTACHMENT_MAX_IMAGE_BYTES = PROVIDER_SEND_TURN_MAX_IMAGE_BYTES;
export const ATTACHMENT_MAX_FILE_BYTES = PROVIDER_SEND_TURN_MAX_FILE_BYTES;

interface AttachmentTypeEntry {
  readonly mimeType: string;
  /** First entry is the canonical extension the store names the file with. */
  readonly extensions: ReadonlyArray<string>;
  readonly kind: AttachmentKind;
}

/**
 * Extensions refused no matter which mime type accompanies them.
 *
 * Not a judgement about the content: a person may legitimately want to hand an
 * agent an HTML fixture or a shell script. It is about the delivery channel —
 * `/attachments/<id>` hands these to a browser, and a browser executes them.
 * A file whose bytes need to reach an agent can go through the repository or a
 * terminal, both of which never serve it back to a viewer.
 */
export const DENIED_ATTACHMENT_EXTENSIONS: ReadonlySet<string> = new Set([
  ".app",
  ".bat",
  ".bash",
  ".cmd",
  ".com",
  ".dll",
  ".dmg",
  ".exe",
  ".htm",
  ".html",
  ".jar",
  ".js",
  ".jse",
  ".msi",
  ".mjs",
  ".pkg",
  ".ps1",
  ".scr",
  ".sh",
  ".svg",
  ".svgz",
  ".vbs",
  ".wsf",
  ".xhtml",
  ".zsh",
]);

/**
 * The allowlist itself.
 *
 * Images keep the exact set the providers render; the rest is what people
 * actually paste into a coding thread — a spec, a spreadsheet of failures, a
 * log, a data dump, an archive of logs — plus the office formats, which an
 * agent opens only if it has a tool for them. That last caveat is the user's
 * to weigh, not ours to enforce: refusing the upload does not give them the
 * tool, it just makes them find another way to hand over the file.
 */
const ATTACHMENT_TYPES: ReadonlyArray<AttachmentTypeEntry> = [
  // Images — the four every provider and every browser renders.
  { mimeType: "image/png", extensions: [".png"], kind: "image" },
  { mimeType: "image/jpeg", extensions: [".jpg", ".jpeg"], kind: "image" },
  { mimeType: "image/webp", extensions: [".webp"], kind: "image" },
  { mimeType: "image/gif", extensions: [".gif"], kind: "image" },

  // Documents.
  { mimeType: "application/pdf", extensions: [".pdf"], kind: "document" },
  { mimeType: "application/rtf", extensions: [".rtf"], kind: "document" },
  { mimeType: "application/msword", extensions: [".doc"], kind: "document" },
  {
    mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    extensions: [".docx"],
    kind: "document",
  },
  { mimeType: "application/vnd.ms-excel", extensions: [".xls"], kind: "document" },
  {
    mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    extensions: [".xlsx"],
    kind: "document",
  },
  { mimeType: "application/vnd.ms-powerpoint", extensions: [".ppt"], kind: "document" },
  {
    mimeType: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    extensions: [".pptx"],
    kind: "document",
  },
  {
    mimeType: "application/vnd.oasis.opendocument.text",
    extensions: [".odt"],
    kind: "document",
  },
  {
    mimeType: "application/vnd.oasis.opendocument.spreadsheet",
    extensions: [".ods"],
    kind: "document",
  },
  {
    mimeType: "application/vnd.oasis.opendocument.presentation",
    extensions: [".odp"],
    kind: "document",
  },

  // Text and data. The extension list is what makes a dropped source file
  // work: browsers report most of these as `text/plain` or as nothing at all.
  {
    mimeType: "text/plain",
    extensions: [
      ".txt",
      ".log",
      ".text",
      ".env",
      ".ini",
      ".cfg",
      ".conf",
      ".properties",
      ".diff",
      ".patch",
      ".sql",
      ".graphql",
      ".gql",
      ".proto",
      ".c",
      ".h",
      ".cc",
      ".cpp",
      ".hpp",
      ".cs",
      ".go",
      ".java",
      ".kt",
      ".kts",
      ".m",
      ".mm",
      ".php",
      ".py",
      ".rb",
      ".rs",
      ".scala",
      ".swift",
      ".ts",
      ".tsx",
      ".jsx",
      ".vue",
      ".svelte",
      ".lua",
      ".pl",
      ".r",
      ".dart",
      ".ex",
      ".exs",
      ".erl",
      ".hs",
      ".clj",
      ".tf",
      ".tfvars",
      ".dockerfile",
      ".gradle",
      ".make",
      ".mk",
      ".cmake",
    ],
    kind: "text",
  },
  { mimeType: "text/markdown", extensions: [".md", ".markdown", ".mdx"], kind: "text" },
  { mimeType: "text/csv", extensions: [".csv"], kind: "text" },
  { mimeType: "text/tab-separated-values", extensions: [".tsv"], kind: "text" },
  { mimeType: "application/json", extensions: [".json", ".jsonl", ".ndjson"], kind: "text" },
  { mimeType: "application/xml", extensions: [".xml"], kind: "text" },
  { mimeType: "application/x-yaml", extensions: [".yaml", ".yml"], kind: "text" },
  { mimeType: "application/toml", extensions: [".toml"], kind: "text" },
  { mimeType: "text/css", extensions: [".css", ".scss", ".sass", ".less"], kind: "text" },

  // Archives.
  { mimeType: "application/zip", extensions: [".zip"], kind: "archive" },
  { mimeType: "application/gzip", extensions: [".gz", ".tgz"], kind: "archive" },
  { mimeType: "application/x-tar", extensions: [".tar"], kind: "archive" },
  { mimeType: "application/x-bzip2", extensions: [".bz2"], kind: "archive" },
  { mimeType: "application/x-7z-compressed", extensions: [".7z"], kind: "archive" },
];

/**
 * Mime types clients report for a format whose canonical entry is above.
 *
 * Browsers, operating systems and older clients disagree about half of these —
 * `text/yaml` vs `application/x-yaml`, `application/x-zip-compressed` on
 * Windows, `text/x-markdown` — and a person dropping a `.yml` does not care
 * which one their OS picked. They collapse onto the canonical mime type before
 * anything else looks at them, so the rest of the system sees one spelling.
 */
const MIME_TYPE_ALIASES: Readonly<Record<string, string>> = {
  "application/x-yaml": "application/x-yaml",
  "application/yaml": "application/x-yaml",
  "text/yaml": "application/x-yaml",
  "text/x-yaml": "application/x-yaml",
  "text/toml": "application/toml",
  "text/markdown": "text/markdown",
  "text/x-markdown": "text/markdown",
  "text/xml": "application/xml",
  "text/json": "application/json",
  "application/x-ndjson": "application/json",
  "application/jsonl": "application/json",
  "application/x-zip-compressed": "application/zip",
  "multipart/x-zip": "application/zip",
  "application/x-gzip": "application/gzip",
  "application/x-tgz": "application/gzip",
  "application/x-compressed-tar": "application/gzip",
  "application/tar": "application/x-tar",
  "application/x-rtf": "application/rtf",
  "text/rtf": "application/rtf",
  "image/jpg": "image/jpeg",
  "image/pjpeg": "image/jpeg",
  "text/comma-separated-values": "text/csv",
  "application/csv": "text/csv",
  "text/tsv": "text/tab-separated-values",
  "text/x-log": "text/plain",
  "text/x-sql": "text/plain",
  "application/sql": "text/plain",
  "application/x-sh": "text/plain",
  "application/x-typescript": "text/plain",
  "text/x-python": "text/plain",
  "text/x-java-source": "text/plain",
  "text/x-c": "text/plain",
  "text/x-csrc": "text/plain",
  "text/x-c++src": "text/plain",
  "text/x-go": "text/plain",
  "text/x-ruby": "text/plain",
  "text/x-rust": "text/plain",
  "text/x-php": "text/plain",
  "text/x-scss": "text/css",
  "text/x-sass": "text/css",
  "text/x-less": "text/css",
};

const ENTRY_BY_MIME_TYPE: ReadonlyMap<string, AttachmentTypeEntry> = new Map(
  ATTACHMENT_TYPES.map((entry) => [entry.mimeType, entry] as const),
);

const ENTRY_BY_EXTENSION: ReadonlyMap<string, AttachmentTypeEntry> = new Map(
  ATTACHMENT_TYPES.flatMap((entry) =>
    entry.extensions.map((extension) => [extension, entry] as const),
  ),
);

/** Extensions the attachment store may name a file with, plus the fallback. */
export const ALLOWED_ATTACHMENT_EXTENSIONS: ReadonlySet<string> = new Set(
  ATTACHMENT_TYPES.flatMap((entry) => entry.extensions),
);

/**
 * `accept` for the composer's file input.
 *
 * Both mime types and extensions, because neither alone covers the picker:
 * macOS matches source files by extension only, while a photo picked from a
 * phone arrives with a mime type and a name the OS invented.
 */
export const ATTACHMENT_FILE_INPUT_ACCEPT: string = [
  ...ATTACHMENT_TYPES.map((entry) => entry.mimeType),
  ...ALLOWED_ATTACHMENT_EXTENSIONS,
].join(",");

/** Lowercased extension of a file name, `""` when it has none. */
export function attachmentFileExtension(fileName: string): string {
  const trimmed = fileName.trim();
  const lastSlash = Math.max(trimmed.lastIndexOf("/"), trimmed.lastIndexOf("\\"));
  const baseName = lastSlash === -1 ? trimmed : trimmed.slice(lastSlash + 1);
  const match = /\.([A-Za-z0-9]{1,12})$/.exec(baseName);
  return match ? `.${match[1]!.toLowerCase()}` : "";
}

/** Collapse a client-reported mime type onto its canonical spelling. */
export function normalizeAttachmentMimeType(mimeType: string): string {
  const normalized = mimeType.trim().toLowerCase().split(";")[0]?.trim() ?? "";
  return MIME_TYPE_ALIASES[normalized] ?? normalized;
}

export interface AttachmentTypeVerdict {
  readonly kind: AttachmentKind;
  /** Canonical mime type — what gets persisted and served, not what was sent. */
  readonly mimeType: string;
  /** Extension the file is stored under. */
  readonly extension: string;
}

export type AttachmentTypeRejection =
  | { readonly reason: "denied-extension"; readonly extension: string }
  | { readonly reason: "unsupported" }
  | { readonly reason: "mime-name-mismatch"; readonly extension: string };

export type AttachmentTypeResolution =
  | { readonly ok: true; readonly verdict: AttachmentTypeVerdict }
  | { readonly ok: false; readonly rejection: AttachmentTypeRejection };

/**
 * Decide whether a (mime type, file name) pair may be attached, and under what
 * canonical type it is stored.
 *
 * The name is consulted first for anything the mime type leaves ambiguous,
 * which is most source files: a dropped `.rs` arrives as `""` or
 * `application/octet-stream`, and refusing it because the browser had no
 * opinion would make the allowlist useless for the files people attach most.
 * Where both are known and disagree, that is the mismatch case — not a
 * tie-break.
 */
export function resolveAttachmentType(input: {
  readonly mimeType: string;
  readonly fileName: string;
}): AttachmentTypeResolution {
  const extension = attachmentFileExtension(input.fileName);
  if (extension.length > 0 && DENIED_ATTACHMENT_EXTENSIONS.has(extension)) {
    return { ok: false, rejection: { reason: "denied-extension", extension } };
  }

  const mimeType = normalizeAttachmentMimeType(input.mimeType);
  const byMimeType = ENTRY_BY_MIME_TYPE.get(mimeType);
  const byExtension = extension.length > 0 ? ENTRY_BY_EXTENSION.get(extension) : undefined;

  // Both recognized and pointing at different formats: the extension and the
  // content-type would disagree on disk, so neither is trustworthy.
  if (byMimeType && byExtension && byMimeType.mimeType !== byExtension.mimeType) {
    return { ok: false, rejection: { reason: "mime-name-mismatch", extension } };
  }

  const entry = byMimeType ?? byExtension;
  if (!entry) {
    return { ok: false, rejection: { reason: "unsupported" } };
  }

  return {
    ok: true,
    verdict: {
      kind: entry.kind,
      mimeType: entry.mimeType,
      // Keep the name's own spelling when the format claims it (`.jpeg` stays
      // `.jpeg`), so a download hands back the file the person uploaded.
      extension: byExtension ? extension : entry.extensions[0]!,
    },
  };
}

/** Byte ceiling for a kind. Images are capped by the model, files by transport. */
export function attachmentMaxBytes(kind: AttachmentKind): number {
  return kind === "image" ? ATTACHMENT_MAX_IMAGE_BYTES : ATTACHMENT_MAX_FILE_BYTES;
}

/**
 * Whether the HTTP route may let a browser render this inline.
 *
 * Only the four image formats. Everything else — PDFs included, since a PDF
 * viewer is a scripting host — is served as a download, so a link to
 * `/attachments/<id>` can never execute anything on the app's origin.
 */
export function attachmentIsInlineRenderable(mimeType: string): boolean {
  const entry = ENTRY_BY_MIME_TYPE.get(normalizeAttachmentMimeType(mimeType));
  return entry?.kind === "image";
}

/** Human-readable size for chips, prompts and error messages. */
export function formatAttachmentSize(sizeBytes: number): string {
  if (!Number.isFinite(sizeBytes) || sizeBytes <= 0) return "0 B";
  if (sizeBytes < 1024) return `${sizeBytes} B`;
  const units = ["KB", "MB", "GB"] as const;
  let value = sizeBytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value >= 10 ? Math.round(value) : Math.round(value * 10) / 10} ${units[unitIndex]}`;
}
